import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Loader2, FileText, CheckCircle2, ExternalLink, Crown, ArrowRight } from 'lucide-react';
import axios from 'axios';
import API from '@/utils/api';
import StudioShell from '@/components/studio/StudioShell';

export default function PricingPage() {
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [subRes, invRes] = await Promise.all([
          axios.get(`${API}/subscription/status`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/billing/invoices`, { headers }).catch(() => ({ data: { invoices: [] } })),
        ]);
        setSub(subRes.data);
        setInvoices(invRes.data?.invoices || []);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, []); // eslint-disable-line

  if (loading) {
    return (
      <StudioShell mode="workspace" activeKey="settings" contextLabel="Plan i płatności">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      </StudioShell>
    );
  }

  const planName = sub?.plan === 'done_for_you' ? 'Zróbcie to za mnie' : sub?.plan === 'diy' ? 'Chcę działać sam' : 'Plan testowy (darmowy)';
  const isActive = sub?.active;

  return (
    <StudioShell mode="workspace" activeKey="settings" contextLabel="Plan i płatności">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 space-y-6" data-testid="billing-page">
        {/* Header */}
        <header className="space-y-1">
          <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight" data-testid="billing-title">
            Plan i płatności
          </h1>
          <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-base">
            Zarządzaj swoim planem APISAI i przeglądaj historię płatności.
          </p>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          {/* ─── Kolumna główna ─── */}
          <div className="space-y-6 min-w-0">
            {/* Aktualny plan */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="apisai-soft-card p-5 sm:p-6" data-testid="current-plan-section">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(139,92,246,0.1)' }}>
                    <Crown className="w-5 h-5" style={{ color: '#8B5CF6' }} />
                  </div>
                  <div>
                    <p className="text-[var(--apisai-text-muted)] text-[11px] uppercase font-bold tracking-wider mb-0.5">Aktualny plan</p>
                    <p className="text-[var(--apisai-text-primary)] text-lg font-bold">{planName}</p>
                    {isActive && sub?.days_remaining && (
                      <p className="text-[var(--apisai-text-muted)] text-xs mt-0.5">Następna płatność za {sub.days_remaining} dni</p>
                    )}
                    {sub?.is_trial && (
                      <p className="text-[var(--apisai-primary)] text-xs mt-0.5">Okres próbny — {sub.trial_days_remaining} dni pozostało</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/panel/subskrypcja')}
                  data-testid="change-plan-btn"
                  className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl text-[13px] font-semibold text-white transition-all hover:brightness-95"
                  style={{ background: 'var(--apisai-primary)' }}>
                  Zmień plan <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* Metoda płatności */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="apisai-soft-card p-5 sm:p-6" data-testid="payment-method-section">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16,185,129,0.1)' }}>
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[var(--apisai-text-muted)] text-[11px] uppercase font-bold tracking-wider mb-0.5">Metoda płatności</p>
                  <p className="text-[var(--apisai-text-secondary)] text-sm">
                    {sub?.payment_method ? sub.payment_method : 'Zarządzane przez Stripe'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Historia płatności */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="apisai-soft-card p-5 sm:p-6" data-testid="invoices-section">
              <div className="flex items-center gap-2.5 mb-4">
                <FileText className="w-4 h-4 text-[var(--apisai-text-muted)]" />
                <p className="text-[var(--apisai-text-primary)] text-sm font-bold">Historia płatności</p>
              </div>

              {invoices.length === 0 ? (
                <p className="text-[var(--apisai-text-muted)] text-sm text-center py-6">Brak historii płatności</p>
              ) : (
                <div className="space-y-3">
                  {invoices.map((inv, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${inv.status === 'paid' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        <div>
                          <p className="text-sm font-medium text-[var(--apisai-text-primary)]">{inv.amount} zł</p>
                          <p className="text-xs text-[var(--apisai-text-muted)]">{new Date(inv.date).toLocaleDateString('pl-PL')}</p>
                        </div>
                      </div>
                      {inv.invoice_url && (
                        <a href={inv.invoice_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--apisai-primary)] hover:underline flex items-center gap-1">
                          Faktura <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* ─── Prawy rail ─── */}
          <aside className="space-y-4" data-testid="billing-rail">
            <div className="apisai-soft-card p-5">
              <p className="text-[var(--apisai-text-primary)] text-sm font-bold mb-3">Co daje plan PRO?</p>
              <ul className="space-y-2.5">
                {['Publikacja bez limitów', 'Więcej zapytań o szczeniaki', 'Priorytet w katalogu APISAI', 'Pełny dostęp do APIS'].map((b) => (
                  <li key={b} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500 flex-shrink-0" />
                    <span className="text-[13px] text-[var(--apisai-text-secondary)]">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="apisai-soft-card p-5" data-testid="billing-help">
              <p className="text-[var(--apisai-text-primary)] text-sm font-bold">Potrzebujesz pomocy?</p>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-1">Asystent ApisAI odpowie na każde pytanie o platformę.</p>
              <button onClick={() => navigate('/panel/pomoc')} className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--apisai-primary)] hover:underline">
                Otwórz pomoc <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </aside>
        </div>
      </div>
    </StudioShell>
  );
}
